// src/loaders/index.js
import { loadOBJ } from './objLoader'
import { loadPLY, loadPLYGeometryOnly } from './plyLoader'
import { loadSTL, loadSTLGeometryOnly } from './stlLoader'

const registry = {
  '.obj': loadOBJ,
  '.ply': loadPLY,
  '.stl': loadSTL,
}

const geometryOnlyRegistry = {
  '.ply': loadPLYGeometryOnly,
  '.stl': loadSTLGeometryOnly,
}

/**
 * Load a 3D model from a path or uploaded file data
 * @param {string|Object} pathOrData - URL/path ending in .obj/.ply/.stl or object with { type, data }
 * @returns {Promise<THREE.Mesh>}
 */
export function loadModel(pathOrData) {
  // Handle uploaded file data (object) vs path (string)
  if (typeof pathOrData === 'object') {
    // New format: { type: 'stl'|'ply'|'obj', data: blobUrl|{ objUrl, mtlUrl, textureUrls } }
    if (pathOrData.type && pathOrData.data !== undefined) {
      if (pathOrData.type === 'stl') {
        return loadSTL(pathOrData.data)
      } else if (pathOrData.type === 'ply') {
        return loadPLY(pathOrData.data)
      } else if (pathOrData.type === 'obj') {
        return loadOBJ(pathOrData.data)
      }
      return Promise.reject(new Error(`Unknown file type: ${pathOrData.type}`))
    }

    // Legacy format for OBJ: { objUrl, mtlUrl, textureUrls }
    if (pathOrData.objUrl) {
      return loadOBJ(pathOrData)
    }

    return Promise.reject(new Error('Invalid file data format'))
  }

  // Original behavior for file paths
  const ext = pathOrData.slice(pathOrData.lastIndexOf('.')).toLowerCase()
  const loaderFn = registry[ext]
  if (!loaderFn) {
    return Promise.reject(new Error(`No loader for ${ext}`))
  }
  return loaderFn(pathOrData)
}

/**
 * Load model to geometry only (parse + center). STL/PLY only; used by phased loader.
 * For OBJ, use loadModel (full pipeline) from loadModelPhased.
 * @param {string|Object} pathOrData - URL/path or { type: 'stl'|'ply', data }
 * @param {Object} [opts] - Options forwarded to underlying loader (e.g. { skipOrientation })
 * @returns {Promise<THREE.BufferGeometry>}
 */
export function loadGeometryOnly(pathOrData, opts = {}) {
  if (typeof pathOrData === 'object' && pathOrData.type && pathOrData.data !== undefined) {
    if (pathOrData.type === 'stl') return loadSTLGeometryOnly(pathOrData.data, opts)
    if (pathOrData.type === 'ply') return loadPLYGeometryOnly(pathOrData.data, opts)
    return Promise.reject(new Error(`loadGeometryOnly does not support type: ${pathOrData.type}`))
  }
  const ext = pathOrData.slice(pathOrData.lastIndexOf('.')).toLowerCase()
  const fn = geometryOnlyRegistry[ext]
  if (!fn) return Promise.reject(new Error(`No geometry-only loader for ${ext}`))
  return fn(pathOrData, opts)
}